import { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FlaskConical, Rocket, Globe, AppWindow } from 'lucide-react';
import { updateStore, listStoreUsers, checkOrderNumberPrefix } from '../../../utils/queries';
import Switch from '../../../components/Switch';
import Tooltip from '../../../components/Tooltip';
import ModeCard from '../../../components/ModeCard';
import ApproverSelectionModal from '../../../components/ApproverSelectionModal';
import { MyContext } from '../../../context';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

export default function GeneralTab({ store, onUpdated }) {
	const { refreshStores } = useContext(MyContext);
	const canManage = useCan(PLATFORMADMIN_ROLES);
	const [values, setValues] = useState({
		storeName: store.storeName || '',
		storeDomain: store.storeDomain || '',
		orderNumberPrefix: store.orderNumberPrefix || '',
		activeFlag: !!store.activeFlag,
		allowGuestCheckout: !!store.allowGuestCheckout,
		requireOrderApproval: !!store.requireOrderApproval,
		requireOrderReview: !!store.requireOrderReview,
		showHomePage: !!store.showHomePage,
		mode: store.mode || 'test',
		storefrontType: store.storefrontType || 'standalone',
		delayedShipmentThresholdDays: store.delayedShipmentThresholdDays ?? 3,
		reorderReminderThresholdDays: store.reorderReminderThresholdDays ?? 60,
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	// On-blur uniqueness check for the order number prefix: idle | checking |
	// available | taken | invalid. Save re-checks server-side too (409), this
	// just surfaces a collision before the admin hits Save.
	const [prefixCheck, setPrefixCheck] = useState({ status: 'idle', message: '' });
	const latestPrefix = useRef(values.orderNumberPrefix);
	latestPrefix.current = values.orderNumberPrefix;

	// `store` only changes here after a switch (StoreSettingsPage re-fetching
	// on activeStoreId) or a successful save (onUpdated = setStore, passed the
	// fresh server response) -- both cases mean local form state is stale and
	// needs to be resynced, otherwise a just-saved toggle like
	// requireOrderApproval keeps showing its pre-save value until a full page
	// reload remounts this component.
	useEffect(() => {
		setValues({
			storeName: store.storeName || '',
			storeDomain: store.storeDomain || '',
			orderNumberPrefix: store.orderNumberPrefix || '',
			activeFlag: !!store.activeFlag,
			allowGuestCheckout: !!store.allowGuestCheckout,
			requireOrderApproval: !!store.requireOrderApproval,
			requireOrderReview: !!store.requireOrderReview,
			showHomePage: !!store.showHomePage,
			mode: store.mode || 'test',
			storefrontType: store.storefrontType || 'standalone',
			delayedShipmentThresholdDays: store.delayedShipmentThresholdDays ?? 3,
			reorderReminderThresholdDays: store.reorderReminderThresholdDays ?? 60,
		});
		setPrefixCheck({ status: 'idle', message: '' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store]);

	// Lightweight approver-count check, only fetched while the toggle is on --
	// drives the "Manage Approvers (n)" link text, not a warning. Re-fetched
	// after the modal saves so the count reflects the latest selection.
	const [approverCount, setApproverCount] = useState(null);
	const [approverModalOpen, setApproverModalOpen] = useState(false);

	const refreshApproverCount = () => {
		listStoreUsers({ storeId: store.storeId, limit: 200 }).then((result) => {
			if (!result?.success) return;
			const count = (result.data.users || []).filter((u) => u.isOrderApprover && u.activeFlag).length;
			setApproverCount(count);
		});
	};

	useEffect(() => {
		if (!values.requireOrderApproval) return;
		refreshApproverCount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.requireOrderApproval, store.storeId]);

	const handleRequireApprovalChange = (checked) => {
		setValues((current) => ({ ...current, requireOrderApproval: checked }));
		// Turning it on is the moment an admin needs to actually pick
		// approvers, so open the picker right away instead of leaving them to
		// discover Users & Permissions on their own.
		if (checked) setApproverModalOpen(true);
	};

	const handlePrefixBlur = async () => {
		const prefix = values.orderNumberPrefix;
		// Blank (feature off) and the store's own saved prefix need no check.
		if (!prefix || prefix === (store.orderNumberPrefix || '')) {
			setPrefixCheck({ status: 'idle', message: '' });
			return;
		}
		setPrefixCheck({ status: 'checking', message: '' });
		const result = await checkOrderNumberPrefix(store.storeId, prefix);
		// The admin may have kept typing while the request was in flight.
		if (prefix !== latestPrefix.current) return;
		if (!result?.success) {
			setPrefixCheck({ status: 'invalid', message: result?.error?.message || 'Could not check this prefix.' });
			return;
		}
		setPrefixCheck(
			result.data.available
				? { status: 'available', message: 'Available' }
				: { status: 'taken', message: 'Already used by another store' },
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const result = await updateStore(store.storeId, values);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		toast.success('Successfully saved your changes.');
		onUpdated?.(result.data);
		// This form's own state is already resynced via the effect above, but
		// the global `stores` array in context.js is loaded once at login and
		// otherwise never refreshed -- without this, the orders list's
		// "Needs approval" view (views/orders/index.js) keeps reading the
		// pre-save requireOrderApproval value until a full page reload.
		refreshStores();
	};

	return (
		<>
		<form onSubmit={handleSubmit} className='space-y-4 max-w-2xl'>
			{error && <div className='text-sm text-pi-red'>{error}</div>}
			<div>
				<label>Store Name</label>
				<input
					className='form-input'
					required
					value={values.storeName}
					onChange={(e) => setValues({ ...values, storeName: e.target.value })}
				/>
				<p className='text-xs text-gray-400'>Shown in order confirmation emails, footer, and across your storefront.</p>
			</div>
			{/* <div>
				<label>Store Code</label>
				<input className='form-input bg-gray-100' value={store.storeCode} disabled />
			</div>
			*/}
			<div className='flex items-center justify-between gap-4 py-4'>
				<div>
					<label>URL/Domain</label>
					<input
						className='form-input w-96'
						value={values.storeDomain}
						onChange={(e) => setValues({ ...values, storeDomain: e.target.value })}
					/>
				</div>
				<div>
					<label>Order Number Prefix</label>
					<input
						className='form-input w-32 uppercase'
						maxLength={10}
						value={values.orderNumberPrefix}
						onChange={(e) => {
							setValues({ ...values, orderNumberPrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') });
							setPrefixCheck({ status: 'idle', message: '' });
						}}
						onBlur={handlePrefixBlur}
					/>
					{prefixCheck.status === 'checking' && <p className='text-xs text-gray-400'>Checking…</p>}
					{prefixCheck.status === 'available' && <p className='text-xs text-green-700'>{prefixCheck.message}</p>}
					{(prefixCheck.status === 'taken' || prefixCheck.status === 'invalid') && (
						<p className='text-xs text-pi-red'>{prefixCheck.message}</p>
					)}
					<p className='text-xs text-gray-400'>
						{values.orderNumberPrefix
							? `Next order will be ${values.orderNumberPrefix}-${String((store.lastOrderSequence ?? 0) + 1).padStart(5, '0')}.`
							: "Orders will be numbered PREFIX-00001, PREFIX-00002… Leave blank to use the storefront's own numbering."}
					</p>
				</div>
			</div>
			<div className='pt-3 divide-y divide-gray-100 border-y border-gray-100'>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Allow checkout as guest</p>
						<p className='text-sm text-gray-400'>
							When off, visitors must create an account or log in to view this store.
						</p>
					</div>
					<Switch
						checked={values.allowGuestCheckout}
						onChange={(checked) => setValues({ ...values, allowGuestCheckout: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Require order approval</p>
						<p className='text-sm text-gray-400'>
							When on, new orders require a defined approver to approve or decline them.
						</p>
						{values.requireOrderApproval && (
							<button
								type='button'
								className='text-gray-400 text-xs hover:text-pi-red underline mt-1'
								onClick={() => setApproverModalOpen(true)}
							>
								View Approvers{approverCount != null ? ` (${approverCount})` : ''}
							</button>
						)}
					</div>
					<Switch checked={values.requireOrderApproval} onChange={handleRequireApprovalChange} />
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Manual review of each order is required</p>
						<p className='text-sm text-gray-400'>
							When on, every order waits in Pending Review until released to Pace.
						</p>
					</div>
					<Switch
						checked={values.requireOrderReview}
						onChange={(checked) => setValues({ ...values, requireOrderReview: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<label className='text-sm'>Delayed shipment after (days)</label>
						<p className='text-sm text-gray-400 mb-2'>
							Controls when an order is flagged as delayed.
						</p>
					</div>
					<input
						type='number'
						min='1'
						className='form-input w-16'
						value={values.delayedShipmentThresholdDays}
						onChange={(e) =>
							setValues({ ...values, delayedShipmentThresholdDays: Number(e.target.value) })
						}
					/>
				</div>
				{/* <div className='flex items-center justify-between gap-4 py-4'>
					<label className='text-sm'>Reorder reminder after (days)</label>
					<p className='text-sm text-gray-400 mb-2'>
						Controls when a customer is flagged as overdue to reorder.
					</p>
					<input
						type='number'
						min='1'
						className='form-input w-16'
						value={values.reorderReminderThresholdDays}
						onChange={(e) =>
							setValues({ ...values, reorderReminderThresholdDays: Number(e.target.value) })
						}
					/>
				</div> */}
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Show the Home page</p>
						<p className='text-sm text-gray-400'>
							Show the marketing-style home page and its menu link on your storefront.
						</p>
					</div>
					<Switch
						checked={values.showHomePage}
						onChange={(checked) => setValues({ ...values, showHomePage: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Active</p>
					</div>
					<Switch
						checked={values.activeFlag}
						onChange={(checked) => setValues({ ...values, activeFlag: checked })}
					/>
				</div>
			</div>
			{canManage && (
				<div className='pt-4'>
					<label>Mode</label>
					<div className='grid grid-cols-2 gap-4 mt-1'>
						<ModeCard
							selected={values.mode !== 'live'}
							current={values.mode !== 'live'}
							onClick={() => setValues({ ...values, mode: 'test' })}
							icon={<FlaskConical className='size-4 text-gray-600' />}
							label='Test Mode'
							description="Emails, webhooks and workflows run, but orders are not submitted to the ERP (Pace)."
						/>
						<ModeCard
							selected={values.mode === 'live'}
							current={values.mode === 'live'}
							onClick={() => setValues({ ...values, mode: 'live' })}
							icon={<Rocket className='size-4 text-gray-600' />}
							label='Live Mode'
							description='Orders are submitted to the connected Pace ERP as real jobs.'
						/>
					</div>
				</div>
			)}
			{canManage && (
				<div className='pt-4'>
					<label>Storefront Type</label>
					<div className='grid grid-cols-2 gap-4 mt-1'>
						<ModeCard
							selected={values.storefrontType !== 'iframe'}
							current={store.storefrontType !== 'iframe'}
							onClick={() => setValues({ ...values, storefrontType: 'standalone' })}
							icon={<Globe className='size-4 text-gray-600' />}
							label='Standalone'
							description='Its own website: customers browse to the store domain directly.'
						/>
						<ModeCard
							selected={values.storefrontType === 'iframe'}
							current={store.storefrontType === 'iframe'}
							onClick={() => setValues({ ...values, storefrontType: 'iframe' })}
							icon={<AppWindow className='size-4 text-gray-600' />}
							label='Iframe'
							description='Embedded in a host app that signs members in with a JWT. Configure it in Developer > Iframe.'
						/>
					</div>
				</div>
			)}
			<div className="pt-6 flex items-center gap-2">
				<button
					type='submit'
					disabled={saving || !canManage || prefixCheck.status === 'taken' || prefixCheck.status === 'checking'}
					className='btn-primary'
				>
				{saving ? 'Saving...' : 'Save'}
				</button>
				{!canManage && <Tooltip content='Only a Platform Administrator can change store settings.' />}
			</div>
		</form>
		<ApproverSelectionModal
			open={approverModalOpen}
			setOpen={setApproverModalOpen}
			storeId={store.storeId}
			onSaved={refreshApproverCount}
		/>
		</>
	);
}
